import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { FocusContext, itemOffset, useFocusable, useVirtualRow } from '../focus';
import {
  CARD_GAP,
  CARD_SIZE,
  cardHeight,
  type CardVariant,
  CONTENT_WIDTH,
  FOCUS_BLEED,
} from '../styles/tokens';
import { cx } from './cx';
import styles from './Row.module.css';

/**
 * The viewport's content box has always been `itemHeight − 16` × `viewport − 16` (it used to be
 * an 8 px inset of the given size). It now keeps that content box and grows its clip by
 * `FOCUS_BLEED` on every side, so the row's flow height and the cards' positions are unchanged
 * while a focused card's ring and scale fit inside the clip.
 */
const LEGACY_INSET = 8;
export function rowViewportSize(size: number): number {
  return size - 2 * LEGACY_INSET + 2 * FOCUS_BLEED;
}

/**
 * The row-height contract: the `itemHeight` whose flow box (`itemHeight − 16`) is exactly the
 * rendered height of the given content. Rows of cards pass `rowItemHeight(cardHeight(variant))`
 * (see `cardRowHeight`), so the row reserves every line a card can draw — two title lines and
 * the subtitle — and the next row never starts on top of a card's "N canciones" line.
 */
export function rowItemHeight(contentHeight: number): number {
  return contentHeight + 2 * LEGACY_INSET;
}

/** `itemHeight` for a row of `Card`s of the given variant and cover size. */
export function cardRowHeight(variant: CardVariant = 'song', size: number = CARD_SIZE): number {
  return rowItemHeight(cardHeight(variant, size));
}

export interface RowItemRenderArgs {
  index: number;
  /** Stable focus key for the item (`${rowKey}-${index}`); pass it to the item component. */
  focusKey: string;
  /** Must be forwarded to the item so the row can follow the focus. */
  onFocus: () => void;
}

export interface RowProps {
  title?: string;
  count: number;
  renderItem: (args: RowItemRenderArgs) => ReactNode;
  focusKey: string;
  /** Item extent along the row (px). Default: card size 220. */
  itemWidth?: number;
  /**
   * Rendered height of one item incl. text, plus the 16 px legacy inset (px). Default
   * `cardRowHeight('song')`: cover + two title lines + subtitle. Use `cardRowHeight(variant)`
   * for other card rows; never a hand-tuned number.
   */
  itemHeight?: number;
  gap?: number;
  /** Visible width (px). Default: content width 1476. */
  viewport?: number;
  /** Focus key of the item to prefer on first entry (default: last focused, else first). */
  preferredChildFocusKey?: string;
  /** Called when focus enters the row (vertical scrolling of the screen). */
  onActive?: () => void;
  className?: string;
}

/**
 * Title + horizontally virtualized list (docs/04 §2). Only a window of items is mounted and the
 * track is translated with `transform` so focus movement stays at 60 fps.
 */
export function Row({
  title,
  count,
  renderItem,
  focusKey,
  itemWidth = CARD_SIZE,
  itemHeight = cardRowHeight('song'),
  gap = CARD_GAP,
  viewport = CONTENT_WIDTH,
  preferredChildFocusKey,
  onActive,
  className,
}: RowProps) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const {
    ref,
    focusKey: key,
    hasFocusedChild,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    trackChildren: true,
    saveLastFocusedChild: true,
    preferredChildFocusKey,
  });
  const onActiveRef = useRef(onActive);
  useEffect(() => {
    onActiveRef.current = onActive;
  });
  useEffect(() => {
    if (hasFocusedChild) onActiveRef.current?.();
  }, [hasFocusedChild]);

  const win = useVirtualRow({ count, itemSize: itemWidth, gap, viewport, focusedIndex });
  const focusAt = useCallback((index: number) => setFocusedIndex(index), []);

  const items: ReactNode[] = [];
  for (let i = win.start; i < win.end; i += 1) {
    items.push(
      <div
        key={i}
        className={styles.slot}
        style={{ transform: `translateX(${itemOffset(i, itemWidth, gap)}px)` }}
        data-index={i}
      >
        {renderItem({ index: i, focusKey: `${focusKey}-${i}`, onFocus: () => focusAt(i) })}
      </div>,
    );
  }

  return (
    <FocusContext.Provider value={key}>
      <section
        ref={ref}
        className={cx(styles.row, className)}
        data-active={hasFocusedChild || undefined}
        /* UX-011: the whole row nudges when a press runs off either end. */
        data-edge-cue-target=""
      >
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        <div
          className={styles.viewport}
          style={{ height: rowViewportSize(itemHeight), width: rowViewportSize(viewport) }}
        >
          <div
            className={styles.track}
            style={{
              transform: `translateX(${-win.offset}px)`,
              width: win.total,
              height: itemHeight,
            }}
          >
            {items}
          </div>
        </div>
      </section>
    </FocusContext.Provider>
  );
}
